import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sending, setSending] = useState("");
    const [backingUp, setBackingUp] = useState(false);

    // my-pending -> only you (your billing list). team-pending -> all active users.
    const sendTest = async (which, label) => {
        setSending(which);
        try {
            const { data } = await api.post(`/admin/send-${which}`);
            if (which === "team-pending") {
                toast.success(`${label} sent to all ${data?.recipients ?? ""} active user(s).`);
            } else {
                toast.success(`${label} sent to ${user?.email} — check your inbox.`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending("");
        }
    };

    const runBackup = async () => {
        setBackingUp(true);
        try {
            const { data } = await api.post("/admin/backup");
            toast.success(`Backup written: ${data?.file || "done"}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBackingUp(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (next.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        if (next !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="profile-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Account
                </div>
                <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                    Your profile
                </h1>
            </div>

            <Card className="sdc-card p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Name
                        </div>
                        <div className="mt-1 font-semibold">{user?.name}</div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Email
                        </div>
                        <div className="mt-1 font-mono text-sm">{user?.email}</div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Role
                        </div>
                        <div className="mt-1 font-semibold capitalize">{user?.role}</div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Designation
                        </div>
                        <div className="mt-1">{user?.designation || "—"}</div>
                    </div>
                </div>
            </Card>

            <Card className="sdc-card p-6">
                <h2 className="font-display text-2xl font-extrabold tracking-tight">
                    Change password
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    You'll stay signed in on this device.
                </p>
                <form
                    onSubmit={onSubmit}
                    className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2"
                >
                    <div className="space-y-2 sm:col-span-2">
                        <Label>Current password</Label>
                        <Input
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            required
                            data-testid="profile-current-pw"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>New password</Label>
                        <Input
                            type="password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            required
                            data-testid="profile-new-pw"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Confirm new password</Label>
                        <Input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            data-testid="profile-confirm-pw"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Button type="submit" disabled={submitting} data-testid="profile-save-pw">
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Update password"
                            )}
                        </Button>
                    </div>
                </form>
            </Card>

            {user?.role === "admin" && (
                <Card className="sdc-card p-6">
                    <h2 className="font-display text-2xl font-extrabold tracking-tight">
                        Maintenance
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Admin tools. The two daily mails send automatically — “My Pending Tasks”
                        at 8:30 AM IST and “Pending Tasks of SDC” at 9:00 AM IST; backups run daily
                        on their own. “My Pending Tasks” below sends a copy to you; “Pending Tasks
                        of SDC” sends to all active users right now.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3">
                        <Button
                            variant="outline"
                            onClick={() => sendTest("my-pending", "My Pending Tasks")}
                            disabled={!!sending}
                            data-testid="profile-send-my-pending"
                        >
                            {sending === "my-pending" ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="mr-1.5 h-4 w-4" />
                            )}
                            Send: My Pending Tasks (to me)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => sendTest("team-pending", "Pending Tasks of SDC")}
                            disabled={!!sending}
                            data-testid="profile-send-team-pending"
                        >
                            {sending === "team-pending" ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="mr-1.5 h-4 w-4" />
                            )}
                            Send: Pending Tasks of SDC (all users)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={runBackup}
                            disabled={backingUp}
                            data-testid="profile-backup"
                        >
                            {backingUp ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                                <DatabaseBackup className="mr-1.5 h-4 w-4" />
                            )}
                            Back up now
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}
